import { useEffect, useRef, useState } from 'react';
import { CaseCorrigee } from '../../components/CorrectionFaite.jsx';
import { ChampDescription } from '../../components/ChampDescription.jsx';
import { texteDescription } from '../../lib/texte-enrichi';
import { BoutonRediger } from '../../components/BoutonRediger.jsx';
import { useDemande } from '../../components/Dialogue.jsx';
import { _inp } from '../../components/ui/Inputs.jsx';
import { DEVISE, fmt } from '../../lib/currency';
import { creeDefilementAuBord } from '../../lib/defilement-glisser';
import {
  DUREE_APPUI_LONG,
  TOLERANCE_APPUI,
  ZONE_APPUI_LONG,
  bloqueLeDefilementPendant,
  vibre,
} from '../../lib/appui-long';
import { detectMetier, remEff, remiseLigne } from '../../lib/discounts';
import { palierApplique, prixApplique } from '../../lib/paliers';
import { tauxTvaProposes } from '../../lib/tva';
import { uid } from '../../lib/utils';
import {
  defaitLeBloc,
  estBloc,
  UNITE_BLOC,
  memeTva,
  montantLigne,
  prixSaisi,
  regroupeEnBloc,
} from '../../lib/blocs-lignes';
import { MediasLigne } from '../../components/MediasLigne.jsx';
import { AssistantTravaux } from './AssistantTravaux.jsx';
import { CollerArticles } from './CollerArticles.jsx';
import { FicheArticleLibre } from './FicheArticleLibre.jsx';
import { ScanCodeBarres } from '../../components/ScanCodeBarres.jsx';
import { C } from '../../theme/palette';
import { BAT_PRESETS, metierRayons, metierUnites } from '../../constants/trades';
import { ChampUnite } from '../../components/ui/ChampUnite.jsx';
import { termesOffre } from '../../lib/terminologie';
import { tr, trf } from '../../lib/i18n';

/** Le rayon d'un article du stock qui n'en a pas : celui que montre le Stock. */
const RAYON_SANS_NOM = 'Divers';

export function LignesEditor({
  devisId,
  form,
  setForm,
  catalogue,
  setCatalogue,
  stock,
  clients,
  estMag,
  soc,
  setSoc,
  piece = 'offre',
  texteAssistant,
  setTexteAssistant,
}) {
  // Dans une facture, les boutons parlent de la facture, pas de l'offre.
  const facture = piece === 'facture';
  const [edId, setEdId] = useState(null);
  const [showPick, setShowPick] = useState(false);
  const demande = useDemande();
  const [pickMet, setPickMet] = useState('');
  const [metChoisis, setMetChoisis] = useState([]);
  // Les articles cochés parmi ceux des corps de métier choisis : pas forcément tous.
  const [artChoisis, setArtChoisis] = useState([]);
  // « Nouveau corps de métier » : son nom et un premier article, d'un coup.
  const [formMetier, setFormMetier] = useState(null);
  const [dOpen, setDOpen] = useState({});
  const [q, setQ] = useState('');
  const [pickSel, setPickSel] = useState(null);
  const [draftL, setDraftL] = useState({ q: 1, u: '', p: 0 });
  const [recents, setRecents] = useState([]);
  // « ✍️ Article qui n'est pas dans mon catalogue » : la fiche ouverte, avec
  // le nom déjà tapé dans la recherche.
  const [libre, setLibre] = useState(null);
  // Un seul outil ouvert à la fois sous la recherche : assistant, liste
  // collée ou corps de métier.
  const [outil, setOutil] = useState('');
  // Code-barres tapé ou lu à la douchette (magasin) et code inconnu.
  const [code, setCode] = useState('');
  const [codeInconnu, setCodeInconnu] = useState('');
  const [scanVu, setScanVu] = useState('');
  const [dragId, setDragId] = useState(null);
  const listRef = useRef(null);
  const dragRef = useRef({ id: null });
  // La page défile toute seule quand le doigt approche du bord de l'écran.
  const defile = useRef(null);
  if (!defile.current) defile.current = creeDefilementAuBord();
  useEffect(() => () => defile.current.arrete(), []);
  const pressRef = useRef({ id: null, y0: 0, timer: null, engaged: false, mouse: false });
  // Un glissé qui s'achève sur la ligne déplacée ne doit pas l'ouvrir.
  const vientDeGlisser = useRef(false);
  const cl = (clients || []).find((c) => c.id === form.clientId);
  const remiseFor = (ref) => remiseLigne(cl, ref, estMag);
  const upd = (id, k, v) =>
    setForm((p) => ({ ...p, lignes: p.lignes.map((x) => (x.id === id ? { ...x, [k]: v } : x)) }));
  const del = (id) => setForm((p) => ({ ...p, lignes: p.lignes.filter((x) => x.id !== id) }));
  const effRem = (l) => remEff(l);
  // Le bloc : les lignes cochées deviennent une ligne au prix global.
  const [choixBloc, setChoixBloc] = useState(null);
  // Une case sur chaque article : cocher suffit, sans passer par un bouton.
  // Plus rien de coché, la sélection se referme.
  const cocheBloc = (id) => {
    // L'article ouvert se referme : la barre « Regrouper » reste en vue.
    setEdId(null);
    setFormBloc(null);
    setChoixBloc((c) => {
      const avant = c || [];
      const apres = avant.includes(id) ? avant.filter((x) => x !== id) : [...avant, id];
      return apres.length ? apres : null;
    });
  };
  const peutCocher = (l) => !estBloc(l) && form.lignes.length >= 2;
  // Le nom et le prix du bloc se saisissent ensemble, dans la barre du bloc.
  const [formBloc, setFormBloc] = useState(null);
  const ouvreLeBloc = () => {
    const choisies = form.lignes.filter((l) => choixBloc.includes(l.id));
    if (choisies.length < 2) return;
    if (!memeTva(choisies, form.tvaPct)) {
      window.alert(tr('Ces articles n’ont pas le même taux de TVA : fais un bloc par taux.'));
      return;
    }
    const somme = choisies.reduce((t, l) => t + montantLigne(l, effRem(l)), 0);
    setFormBloc({ nom: '', prix: String(Math.round(somme * 100) / 100), erreur: '' });
  };
  const faitLeBloc = () => {
    const nom = (formBloc.nom || '').trim();
    if (!nom) {
      setFormBloc((f) => ({ ...f, erreur: tr('Écris le nom du bloc.') }));
      return;
    }
    const prix = prixSaisi(formBloc.prix);
    if (prix == null) {
      setFormBloc((f) => ({
        ...f,
        erreur: tr('Prix illisible : écris seulement le montant, par exemple 5900.'),
      }));
      return;
    }
    setForm((p) => ({ ...p, lignes: regroupeEnBloc(p.lignes, choixBloc, { nom, prix }) }));
    setChoixBloc(null);
    setFormBloc(null);
  };
  const totalCoche = choixBloc
    ? form.lignes
        .filter((l) => choixBloc.includes(l.id))
        .reduce((t, l) => t + montantLigne(l, effRem(l)), 0)
    : 0;
  // Le prix applique tient compte des tarifs degressifs : a partir d'une
  // certaine quantite, le prix unitaire change. Le prix saisi reste le prix de
  // base ; l'ecraser par celui du palier le perdait des que la quantite
  // repassait sous le seuil.
  const lineTot = (l) => (+l.q || 0) * prixApplique(l) * (1 - effRem(l) / 100);
  const chgQ = (l, val) => {
    const q = ('' + val).replace(',', '.');
    setForm((p) => ({
      ...p,
      lignes: p.lignes.map((x) => (x.id === l.id ? { ...x, q } : x)),
    }));
  };
  const addPalier = async (l) => {
    try {
      const q = await demande('À partir de quelle quantité ?');
      if (!q) return;
      const p = await demande('Prix unitaire à ce palier (' + DEVISE + ') ?');
      if (p == null) return;
      const np = { q: ('' + q).replace(',', '.'), p: ('' + p).replace(',', '.') };
      setForm((pr) => ({
        ...pr,
        lignes: pr.lignes.map((x) =>
          x.id === l.id ? { ...x, paliers: [...(x.paliers || []), np] } : x,
        ),
      }));
    } catch (e) {}
  };
  const delPalier = (l, i) =>
    setForm((pr) => ({
      ...pr,
      lignes: pr.lignes.map((x) =>
        x.id === l.id ? { ...x, paliers: (x.paliers || []).filter((_, j) => j !== i) } : x,
      ),
    }));
  const addLine = (d, u, p, ref) => {
    const rem = ref != null ? remiseFor(ref) : 0;
    const id = uid();
    setForm((prev) => ({
      ...prev,
      lignes: [
        ...prev.lignes,
        { id, d, sd: '', u: u || 'forfait', q: 1, p: p || 0, rem, ref, taxe: true },
      ],
    }));
    setShowPick(false);
    setEdId(id);
  };
  const ouvreLibre = (nom = '') => {
    // La ligne ouverte se referme : la fiche se voit seule, sans défiler.
    setEdId(null);
    setShowPick(false);
    setPickSel(null);
    setOutil('');
    setCodeInconnu('');
    setLibre({ nom });
  };
  const ajouteLibre = (ligne) => {
    setForm((prev) => ({ ...prev, lignes: [...prev.lignes, ligne] }));
    setLibre(null);
    setQ('');
    // La ligne s'ouvre, comme un article du catalogue : sa description se
    // complète ou se rédige tout de suite.
    setEdId(ligne.id);
  };
  const recordRecent = (ref) => {
    if (ref == null) return;
    setRecents((p) => [ref, ...p.filter((x) => x !== ref)].slice(0, 4));
  };
  /**
   * Un code-barres lu (douchette, clavier ou caméra) : l'article du stock
   * entre dans la pièce à son prix, remise du client comprise. Chaque bip
   * ajoute 1 à la quantité de sa ligne.
   */
  const scanne = (brut) => {
    const lu = String(brut || '').trim();
    setCode('');
    if (!lu) return;
    const a = (stock || []).find((x) => x.codeBarre && String(x.codeBarre).trim() === lu);
    if (!a) {
      setCodeInconnu(lu);
      setScanVu('');
      return;
    }
    setCodeInconnu('');
    setForm((prev) => {
      const deja = prev.lignes.find(
        (l) => !estBloc(l) && l.ref != null && String(l.ref) === String(a.ref),
      );
      if (deja) {
        return {
          ...prev,
          lignes: prev.lignes.map((l) =>
            l === deja ? { ...l, q: (parseFloat(String(l.q).replace(',', '.')) || 0) + 1 } : l,
          ),
        };
      }
      return {
        ...prev,
        lignes: [
          ...prev.lignes,
          {
            id: uid(),
            d: a.nom,
            sd: a.description || '',
            u: a.u || 'pièce',
            q: 1,
            p: +a.pv || 0,
            rem: remiseFor(a.ref),
            ref: a.ref,
            taxe: true,
            tva: a.tva != null ? +a.tva : undefined,
          },
        ],
      };
    });
    recordRecent(a.ref);
    setScanVu(a.nom);
  };
  const choosePick = (a) => {
    setPickSel(a);
    setDraftL({ q: 1, u: a.u || 'forfait', p: a.p != null ? a.p : 0 });
  };
  const draftRem = pickSel ? (pickSel.ref != null ? remiseFor(pickSel.ref) : 0) : 0;
  const draftNet = (+draftL.p || 0) * (1 - draftRem / 100);
  const addFromDraft = () => {
    if (!pickSel) return;
    const a = pickSel;
    const id = uid();
    const rem = a.ref != null ? remiseFor(a.ref) : 0;
    setForm((prev) => ({
      ...prev,
      lignes: [
        ...prev.lignes,
        {
          id,
          d: a.nom,
          sd: a.sd || '',
          u: draftL.u || a.u || 'forfait',
          q: +draftL.q || 1,
          p: +draftL.p || 0,
          rem,
          ref: a.ref,
          taxe: true,
          // L'article porte son taux ; sans taux propre, la ligne reste
          // indefinie et retombe sur celui du document. Y recopier le taux de
          // la societe figerait la ligne au taux du jour de la saisie.
          tva: a.tva != null ? +a.tva : undefined,
        },
      ],
    }));
    recordRecent(a.ref);
    setPickSel(null);
    setShowPick(false);
    setEdId(id);
    setQ('');
  };
  const createReusable = () => {
    if (!setCatalogue) return;
    const id =
      'A' +
      ((catalogue || []).reduce((m, x) => Math.max(m, +String(x.id).replace(/\D/g, '') || 0), 0) +
        1);
    const a = {
      id,
      metier: pickMet && pickMet.trim() ? pickMet.trim() : detectMetier(q) || 'À classer',
      nom: q.trim(),
      u: 'forfait',
      p: 0,
      sd: '',
    };
    setCatalogue((p) => [...p, a]);
    setQ('');
    choosePick({
      k: 'c' + id,
      nom: a.nom,
      sub: a.metier,
      metier: a.metier,
      u: a.u,
      p: a.p,
      ref: a.id,
      sd: '',
    });
  };
  const ql = (q || '').trim().toLowerCase();
  // Au magasin, l'assistant et la liste collée cherchent dans le stock : la
  // ligne garde la référence de l'article, le stock et la marge suivent.
  const catalogueOutils = estMag
    ? (stock || []).map((a) => ({
        id: a.ref,
        nom: a.nom,
        // La description de l'article du stock passe sur la ligne.
        sd: a.description || '',
        u: a.u,
        p: a.pv,
        metier: a.cat || RAYON_SANS_NOM,
        tva: a.tva,
      }))
    : catalogue;
  // Les corps de métier du catalogue, puis, au bâtiment, les métiers types
  // livrés avec SwissTech : la liste n'est jamais vide, même sans catalogue.
  // Les corps de métier / rayons que le patron a enlevés de la liste (✕).
  const champCaches = estMag ? 'metiersCachesMag' : 'metiersCaches';
  const caches = Array.isArray(soc?.[champCaches]) ? soc[champCaches] : [];
  const metiersTous = [
    ...new Set([
      ...(estMag ? stock || [] : catalogue || []).map((a) =>
        estMag ? a.cat || RAYON_SANS_NOM : a.metier || 'Prestations',
      ),
      // Les corps de métier / rayons des Réglages, puis, au magasin, la vente
      // de matériaux.
      ...metierRayons(soc, estMag),
      ...(estMag ? ['Vente de matériaux'] : BAT_PRESETS.map((g) => g.metier)),
    ]),
  ].filter((m) =>
    // Seulement les corps de métier / rayons qui ont des articles : un rayon
    // vide n'affichait qu'un bouton grisé « Déjà dans l'offre ».
    estMag
      ? (stock || []).some((a) => (a.cat || RAYON_SANS_NOM) === m)
      : (catalogue || []).some((a) => (a.metier || 'Prestations') === m) ||
        BAT_PRESETS.some((g) => g.metier === m && (g.arts || []).length > 0),
  );
  const metiersDispo = metiersTous.filter((m) => !caches.includes(m));
  const metiersEnleves = metiersTous.filter((m) => caches.includes(m));
  // Enlever un corps de métier ne touche pas à ses articles : il ne s'affiche
  // plus dans la liste, et se remet d'un toucher.
  const enleveMetier = (m) => {
    if (!setSoc) return;
    setSoc((s) => {
      const avant = Array.isArray(s?.[champCaches]) ? s[champCaches] : [];
      return avant.includes(m) ? s : { ...s, [champCaches]: [...avant, m] };
    });
    setMetChoisis((c) => c.filter((x) => x !== m));
  };
  const remetMetier = (m) => {
    if (!setSoc) return;
    setSoc((s) => ({
      ...s,
      [champCaches]: (Array.isArray(s?.[champCaches]) ? s[champCaches] : []).filter((x) => x !== m),
    }));
  };
  const dispo = [
    ...(estMag ? [] : catalogue || []).map((a) => ({
      k: 'c' + a.id,
      nom: a.nom,
      sub: a.metier,
      metier: a.metier || 'Prestations',
      sd: a.sd || '',
      u: a.u,
      p: a.p,
      ref: a.id,
    })),
    ...(estMag ? stock || [] : []).map((a) => ({
      k: 's' + a.ref,
      nom: a.nom,
      sub: a.cat,
      metier: a.cat || RAYON_SANS_NOM,
      sd: a.description || '',
      u: a.u,
      p: a.pv,
      ref: a.ref,
      stk: a.stock,
      mn: a.min,
      tva: a.tva,
      code: a.codeBarre || '',
    })),
  ].filter(
    (a) =>
      (!pickMet || a.metier === pickMet) &&
      (!ql ||
        (a.nom || '').toLowerCase().includes(ql) ||
        (a.metier || '').toLowerCase().includes(ql) ||
        (a.sd || '').toLowerCase().includes(ql) ||
        // La référence ou le code-barres de l'article du stock, comme sur
        // l'ancienne liste du magasin.
        String(a.ref ?? '')
          .toLowerCase()
          .includes(ql) ||
        (a.code || '').includes(ql)),
  );
  /** Les articles du catalogue de l'entreprise rangés dans ce corps de métier. */
  const articlesCatalogue = (m) =>
    (catalogue || [])
      .filter((a) => (a.metier || 'Prestations') === m)
      .map((a) => ({ nom: a.nom, sd: a.sd || '', u: a.u, p: a.p, ref: a.id, tva: a.tva }));
  /** Les articles d'un corps de métier, comme le sélecteur les présente. */
  const articlesDuMetier = (m) =>
    estMag
      ? (stock || [])
          .filter((a) => (a.cat || RAYON_SANS_NOM) === m)
          .map((a) => ({
            nom: a.nom,
            sd: a.description || '',
            u: a.u,
            p: a.pv,
            ref: a.ref,
            tva: a.tva,
          }))
      : articlesCatalogue(m).length
        ? articlesCatalogue(m)
        : // Un métier type sans article au catalogue : ses articles types, à
          // leur prix indicatif, que l'on ajuste ensuite dans l'offre.
          ((BAT_PRESETS.find((g) => g.metier === m) || {}).arts || []).map((a) => ({
            nom: a.nom,
            sd: '',
            u: a.u,
            p: a.p,
          }));
  // Ceux qui ne figurent pas déjà dans l'offre : cocher deux fois le même
  // métier n'ajoute pas les articles deux fois. Un article type (sans fiche
  // au catalogue) se reconnaît à son nom.
  const cleArticle = (a) => (a.ref != null ? 'r' + a.ref : 'n' + (a.nom || '').toLowerCase());
  const dejaDansLOffre = new Set(
    (form.lignes || []).flatMap((l) => [
      l.ref != null ? 'r' + l.ref : '',
      'n' + (l.d || '').toLowerCase(),
    ]),
  );
  const articlesDesMetiersChoisis = metChoisis
    .flatMap(articlesDuMetier)
    .filter((a, i, tous) => tous.findIndex((b) => cleArticle(b) === cleArticle(a)) === i);
  const aAjouterDesMetiers = articlesDesMetiersChoisis.filter(
    (a) => !dejaDansLOffre.has(cleArticle(a)),
  );
  const creeLeMetier = () => {
    const metier = (formMetier.nom || '').trim();
    const nom = (formMetier.article || '').trim();
    if (!metier || !nom) {
      setFormMetier((f) => ({
        ...f,
        erreur: tr('Écris le nom du corps de métier et de son premier article.'),
      }));
      return;
    }
    const id =
      'A' +
      ((catalogue || []).reduce((m, x) => Math.max(m, +String(x.id).replace(/\D/g, '') || 0), 0) +
        1);
    setCatalogue((p) => [
      ...p,
      { id, metier, nom, u: formMetier.u || 'm²', p: prixSaisi(formMetier.prix) || 0, sd: '' },
    ]);
    // Le nouveau métier s'ouvre : son article est là, prêt à cocher.
    setMetChoisis([metier]);
    setArtChoisis([]);
    setFormMetier(null);
  };
  const choisisDesMetiers = aAjouterDesMetiers.filter((a) => artChoisis.includes(cleArticle(a)));
  /** Ajoute d'un coup les articles cochés des corps de métier choisis. */
  const ajouteLesMetiers = () => {
    const lignes = choisisDesMetiers.map((a) => ({
      id: uid(),
      d: a.nom,
      sd: a.sd,
      u: a.u || 'forfait',
      q: 1,
      p: a.p != null ? +a.p || 0 : 0,
      rem: a.ref != null ? remiseFor(a.ref) : 0,
      ref: a.ref,
      taxe: true,
      tva: a.tva != null ? +a.tva : undefined,
    }));
    if (!lignes.length) return;
    setForm((prev) => ({ ...prev, lignes: [...prev.lignes, ...lignes] }));
    // Le métier reste ouvert : le patron continue d'y prendre des articles, ou
    // touche un autre corps de métier pour voir les siens.
    setArtChoisis([]);
  };
  const labMetier = {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    fontSize: 10.5,
    fontWeight: 800,
    color: C.ink,
  };
  const champMetier = {
    border: '1px solid ' + C.border,
    borderRadius: 9,
    padding: '9px 10px',
    fontSize: 15,
    boxSizing: 'border-box',
    width: '100%',
  };
  const labBloc = {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    fontSize: 10.5,
    fontWeight: 800,
    color: '#fff',
  };
  const champBloc = {
    border: 'none',
    borderRadius: 8,
    padding: '10px 11px',
    fontSize: 15,
    color: C.ink,
    boxSizing: 'border-box',
    width: '100%',
  };
  const lab = {
    fontSize: 9,
    fontWeight: 800,
    color: C.muted,
    textTransform: 'uppercase',
    margin: '0 0 5px',
  };
  const moveLine = (from, to) =>
    setForm((p) => {
      const a = p.lignes.slice();
      if (to < 0) to = 0;
      if (to > a.length - 1) to = a.length - 1;
      const it = a.splice(from, 1)[0];
      a.splice(to, 0, it);
      return { ...p, lignes: a };
    });
  // La ligne sous le doigt, à la hauteur y de l'écran : appelé à chaque
  // mouvement, et après chaque pas du défilement automatique.
  const placeA = (y) => {
    const rows = listRef.current ? listRef.current.querySelectorAll('[data-lid]') : [];
    const ids = Array.prototype.map.call(rows, function (r) {
      return r.getAttribute('data-lid');
    });
    const ci = ids.indexOf(dragRef.current.id);
    if (ci < 0) return;
    let tg = rows.length - 1;
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i].getBoundingClientRect();
      if (y < r.top + r.height / 2) {
        tg = i;
        break;
      }
    }
    if (tg !== ci) moveLine(ci, tg);
  };
  const engage = (id) => {
    pressRef.current.engaged = true;
    dragRef.current = { id: String(id) };
    setEdId(null);
    setDragId(id);
    defile.current.demarre(listRef.current, placeA);
  };
  const rowMove = (e) => {
    const st = pressRef.current;
    if (st.id == null) return;
    if (!st.engaged) {
      // Au doigt, l'article ne se prend qu'après l'appui long : le doigt qui
      // bouge avant fait défiler la page, rien ne se déplace.
      if (st.auDoigt) {
        if (Math.hypot(e.clientX - st.x0, e.clientY - st.y0) > TOLERANCE_APPUI) rowUp();
        return;
      }
      if (Math.abs(e.clientY - st.y0) > 6) engage(st.id);
      else return;
    }
    if (e.cancelable) e.preventDefault();
    defile.current.suit(e.clientY);
    placeA(e.clientY);
  };
  const rowUp = () => {
    const st = pressRef.current;
    clearTimeout(st.minuterie);
    defile.current.arrete();
    window.removeEventListener('pointermove', rowMove);
    window.removeEventListener('pointerup', rowUp);
    window.removeEventListener('pointercancel', rowUp);
    const wasEngaged = st.engaged;
    if (wasEngaged) {
      vientDeGlisser.current = true;
      setTimeout(() => {
        vientDeGlisser.current = false;
      }, 0);
    }
    pressRef.current = { id: null, y0: 0, engaged: false };
    dragRef.current = { id: null };
    setDragId(null);
    // Un toucher bref sans glisser : le clic de la ligne l'ouvre.
  };
  const rowDown = (e, id, forceNow) => {
    const el = e.currentTarget;
    // À la souris, le navigateur prenait le glissé pour celui d'un texte
    // sélectionné et l'annulait : la ligne ne bougeait plus.
    if (e.pointerType === 'mouse' && e.cancelable) e.preventDefault();
    try {
      window.getSelection()?.removeAllRanges();
    } catch (_) {}
    try {
      if (el && el.setPointerCapture) el.setPointerCapture(e.pointerId);
    } catch (_) {}
    const auDoigt = e.pointerType === 'touch';
    pressRef.current = {
      id,
      x0: e.clientX,
      y0: e.clientY,
      engaged: false,
      auDoigt,
      // Au doigt : l'article se soulève après un appui long, sans bouger.
      minuterie: auDoigt
        ? setTimeout(() => {
            if (pressRef.current.id !== id || pressRef.current.engaged) return;
            vibre();
            engage(id);
          }, DUREE_APPUI_LONG)
        : null,
    };
    window.addEventListener('pointermove', rowMove);
    window.addEventListener('pointerup', rowUp);
    window.addEventListener('pointercancel', rowUp);
    if (forceNow && !auDoigt) engage(id);
  };
  // Tant qu'un article est pris au doigt, la page ne défile pas sous lui.
  useEffect(() => bloqueLeDefilementPendant(() => pressRef.current.engaged), []);
  // « Ajouter par corps de métier » sous le bouton, et aussi dans « ajouter un
  // article » ouvert : le patron y retrouve tout au même endroit.
  // Le contenu « corps de métier » : sous le bouton de la pièce, et dans
  // l'outil « 🧰 Corps de métier » du panneau « ajouter un article ».
  const contenuMetiers = (
    <div style={{ marginTop: 7 }}>
      <div style={{ fontSize: 10, color: C.grey, marginBottom: 6, lineHeight: 1.4 }}>
        {tr('Touche un corps de métier : ses articles s’affichent, coche ceux que tu veux.')}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
        {metiersDispo.map((m) => {
          const coche = metChoisis.includes(m);
          return (
            <div
              key={m}
              role="checkbox"
              aria-checked={coche}
              aria-label={m}
              onClick={() => {
                // Un métier à la fois : en toucher un autre montre ses articles.
                setMetChoisis(coche ? [] : [m]);
                setArtChoisis([]);
              }}
              style={{
                padding: '8px 12px',
                borderRadius: 18,
                fontSize: 10.5,
                fontWeight: 700,
                cursor: 'pointer',
                border: '1px solid ' + (coche ? C.teal : C.navy + '33'),
                background: coche ? C.teal : C.navyXL,
                color: coche ? '#fff' : C.navy,
              }}
            >
              {coche ? '✓ ' : ''}
              {m}
              {setSoc && (
                <span
                  role="button"
                  aria-label={trf('Enlever {0} de la liste', m)}
                  title={trf('Enlever {0} de la liste', m)}
                  onClick={(e) => {
                    e.stopPropagation();
                    enleveMetier(m);
                  }}
                  style={{
                    marginLeft: 8,
                    padding: '0 2px',
                    fontWeight: 900,
                    color: coche ? '#fff' : C.grey,
                  }}
                >
                  ✕
                </span>
              )}
            </div>
          );
        })}
        <div
          onClick={async () => {
            // Au bâtiment, un petit formulaire : le métier et son premier article.
            if (!estMag && setCatalogue) {
              setFormMetier((f) =>
                f ? null : { nom: '', article: '', u: 'm²', prix: '', erreur: '' },
              );
              return;
            }
            try {
              const m = await demande(
                'Nouveau corps de métier (ex. Menuiserie, Étanchéité, Jardinage…) :',
              );
              if (m && m.trim()) {
                setPickMet(m.trim());
                setQ('');
                setShowPick(true);
              }
            } catch (e) {}
          }}
          style={{
            padding: '7px 11px',
            borderRadius: 18,
            fontSize: 9.5,
            fontWeight: 800,
            cursor: 'pointer',
            border: '1.5px dashed ' + C.green,
            background: '#fff',
            color: C.green,
          }}
        >
          ＋ Nouveau corps de métier
        </div>
      </div>
      {metiersEnleves.length > 0 && (
        <div
          style={{
            marginTop: 8,
            fontSize: 10,
            color: C.grey,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span>{tr('Enlevés de la liste (tes articles restent) :')}</span>
          {metiersEnleves.map((m) => (
            <span
              key={m}
              role="button"
              aria-label={trf('Remettre {0}', m)}
              onClick={() => remetMetier(m)}
              style={{
                padding: '4px 9px',
                borderRadius: 14,
                border: '1px dashed ' + C.navy + '55',
                color: C.navy,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              ↺ {m}
            </span>
          ))}
        </div>
      )}
      {formMetier && (
        <div
          style={{
            border: '1px solid ' + C.green + '66',
            background: C.greenL,
            borderRadius: 10,
            padding: 10,
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            gap: 7,
          }}
        >
          <label style={labMetier}>
            {tr('Nom du corps de métier')}
            <input
              value={formMetier.nom}
              onChange={(e) => setFormMetier((f) => ({ ...f, nom: e.target.value, erreur: '' }))}
              placeholder={tr('Ex. : Rénovation intérieure')}
              style={champMetier}
            />
          </label>
          <label style={labMetier}>
            {tr('Un premier article (nom)')}
            <input
              value={formMetier.article}
              onChange={(e) =>
                setFormMetier((f) => ({ ...f, article: e.target.value, erreur: '' }))
              }
              placeholder={tr('Ex. : Ponçage parquet')}
              style={champMetier}
            />
          </label>
          <ChampUnite
            value={formMetier.u}
            onChange={(e) =>
              setFormMetier((f) => ({ ...f, u: e && e.target ? e.target.value : e }))
            }
            unites={metierUnites(soc, estMag)}
          />
          <label style={labMetier}>
            {trf('Prix ({0} par {1})', DEVISE, formMetier.u || 'm²')}
            <input
              value={formMetier.prix}
              inputMode="decimal"
              onChange={(e) => setFormMetier((f) => ({ ...f, prix: e.target.value, erreur: '' }))}
              style={champMetier}
            />
          </label>
          {formMetier.erreur && (
            <div role="alert" style={{ fontSize: 11, fontWeight: 800, color: '#a3281b' }}>
              {formMetier.erreur}
            </div>
          )}
          <button
            type="button"
            onClick={creeLeMetier}
            style={{
              border: 'none',
              borderRadius: 10,
              padding: 11,
              fontSize: 12,
              fontWeight: 800,
              cursor: 'pointer',
              background: C.green,
              color: '#fff',
            }}
          >
            {tr('Créer le corps de métier')}
          </button>
        </div>
      )}
      {metChoisis.length > 0 && (
        <div style={{ marginTop: 9 }}>
          {articlesDesMetiersChoisis.length > 0 && (
            <div
              style={{
                border: '1px solid ' + C.border,
                borderRadius: 10,
                background: '#fff',
                overflow: 'hidden',
                marginBottom: 8,
              }}
            >
              {aAjouterDesMetiers.length > 0 && (
                <div
                  onClick={() =>
                    setArtChoisis(
                      choisisDesMetiers.length === aAjouterDesMetiers.length
                        ? []
                        : aAjouterDesMetiers.map(cleArticle),
                    )
                  }
                  style={{
                    padding: '8px 11px',
                    fontSize: 10,
                    fontWeight: 800,
                    color: C.teal,
                    cursor: 'pointer',
                    borderBottom: '1px solid ' + C.border,
                  }}
                >
                  {choisisDesMetiers.length === aAjouterDesMetiers.length
                    ? tr('Tout décocher')
                    : tr('Tout cocher')}
                </div>
              )}
              {articlesDesMetiersChoisis.map((art) => {
                const cle = cleArticle(art);
                const deja = dejaDansLOffre.has(cle);
                const coche = deja || artChoisis.includes(cle);
                return (
                  <label
                    key={cle}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 9,
                      padding: '9px 11px',
                      borderBottom: '1px solid ' + C.border,
                      cursor: deja ? 'default' : 'pointer',
                      opacity: deja ? 0.55 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={coche}
                      disabled={deja}
                      onChange={() =>
                        setArtChoisis((p) => (coche ? p.filter((x) => x !== cle) : [...p, cle]))
                      }
                      style={{ width: 18, height: 18, accentColor: C.teal }}
                    />
                    <span style={{ flex: 1, fontSize: 11.5, fontWeight: 700, color: C.ink }}>
                      {art.nom}
                      {deja && (
                        <span style={{ fontSize: 9.5, color: C.green, fontWeight: 800 }}>
                          {' '}
                          {tr('✓ ajouté')}
                        </span>
                      )}
                    </span>
                    <span style={{ fontSize: 10.5, color: C.navy, whiteSpace: 'nowrap' }}>
                      {DEVISE} {fmt(+art.p || 0)}/{art.u || 'forfait'}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
          <button
            onClick={ajouteLesMetiers}
            disabled={choisisDesMetiers.length === 0}
            style={{
              width: '100%',
              border: 'none',
              borderRadius: 10,
              padding: 11,
              fontSize: 11,
              fontWeight: 800,
              cursor: 'pointer',
              background: choisisDesMetiers.length ? C.green : C.border,
              // Lisible aussi tant que rien n'est coché.
              color: choisisDesMetiers.length ? '#fff' : C.grey,
            }}
          >
            {aAjouterDesMetiers.length === 0
              ? facture
                ? tr('Déjà dans la facture')
                : tr('Déjà dans l’offre')
              : choisisDesMetiers.length
                ? trf('✓ Ajouter {0} article(s)', choisisDesMetiers.length)
                : tr('Coche les articles à ajouter')}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div ref={listRef}>
      {/* Le bloc se fait en cochant les articles : l'explication reste
          visible, pour qu'on sache que le bloc existe même sur une pièce vide. */}
      <div
        style={{
          background: C.navyXL,
          borderRadius: 11,
          padding: '9px 12px',
          marginBottom: 9,
          fontSize: 10.5,
          color: C.ink,
          lineHeight: 1.4,
        }}
      >
        {form.lignes.length >= 2
          ? tr(
              '📦 Bloc à prix global : coche les articles à regrouper. Le client verra le détail, avec un seul prix.',
            )
          : tr(
              '📦 Bloc à prix global : ajoute tes articles, puis coche ceux à regrouper. Le client verra le détail, avec un seul prix.',
            )}
      </div>
      {form.lignes.map((l) => {
        const open = edId === l.id;
        // Le détail d'un bloc se lit en entier : c'est ce que le client achète.
        const long = !estBloc(l) && (l.sd || '').length > 70;
        return (
          <div
            key={l.id}
            data-lid={l.id}
            style={{
              background: C.white,
              border: '1px solid ' + (dragId === l.id ? C.teal : C.border),
              borderRadius: 13,
              padding: '12px 13px',
              marginBottom: 9,
              boxShadow:
                dragId === l.id ? '0 8px 22px rgba(15,22,38,.22)' : '0 1px 3px rgba(15,22,38,.05)',
              opacity: dragId && dragId !== l.id ? 0.97 : 1,
            }}
          >
            {/*
              Le haut de l'article (son nom) et la poignée ☰ le prennent : au
              doigt, après un appui long, l'article se soulève et se déplace ;
              le doigt qui glisse aussitôt fait défiler la page, pour lire les
              articles sans les bouger. À la souris, on le tire. Un toucher
              bref l'ouvre.
            */}
            <div
              onClick={() => {
                if (vientDeGlisser.current) return;
                if (choixBloc && peutCocher(l)) {
                  cocheBloc(l.id);
                  return;
                }
                setEdId((prev) => (prev === l.id ? null : l.id));
              }}
              onPointerDown={choixBloc ? undefined : (e) => rowDown(e, l.id)}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                cursor: choixBloc ? 'pointer' : 'grab',
                ...ZONE_APPUI_LONG,
                touchAction: choixBloc ? 'auto' : 'pan-y',
              }}
              onContextMenu={choixBloc ? undefined : (e) => e.preventDefault()}
            >
              {peutCocher(l) && (
                <input
                  type="checkbox"
                  aria-label={trf('Mettre dans le bloc : {0}', l.d || '')}
                  checked={!!choixBloc && choixBloc.includes(l.id)}
                  onChange={() => cocheBloc(l.id)}
                  onClick={(e) => e.stopPropagation()}
                  style={{ width: 22, height: 22, margin: '3px 2px', flex: '0 0 auto' }}
                />
              )}
              {choixBloc ? null : (
                <div
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    rowDown(e, l.id, true);
                  }}
                  title="Glisser pour déplacer"
                  style={{
                    flex: '0 0 auto',
                    width: 26,
                    height: 28,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: C.muted,
                    fontSize: 15,
                    cursor: 'grab',
                    touchAction: 'pan-y',
                    borderRadius: 7,
                    background: C.navyXL,
                  }}
                >
                  ☰
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 800, color: C.ink, lineHeight: 1.3 }}>
                  {estBloc(l) && (
                    <span
                      style={{
                        fontSize: 9,
                        fontWeight: 800,
                        color: C.navy,
                        background: C.navyXL,
                        borderRadius: 20,
                        padding: '2px 7px',
                        marginRight: 6,
                        verticalAlign: 'middle',
                      }}
                    >
                      {tr('📦 Bloc')}
                    </span>
                  )}
                  {l.d || '(article)'}
                </div>
                {!!(l.sd || '') && (
                  <div
                    style={{
                      fontSize: 10.5,
                      color: C.ink,
                      lineHeight: 1.45,
                      marginTop: 3,
                      maxHeight: long && !dOpen[l.id] ? 31 : 'none',
                      overflow: 'hidden',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {texteDescription(l.sd)}
                  </div>
                )}
                {long && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      setDOpen((o) => ({ ...o, [l.id]: !o[l.id] }));
                    }}
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: C.navy,
                      cursor: 'pointer',
                      display: 'inline-block',
                      marginTop: 2,
                    }}
                  >
                    {dOpen[l.id] ? 'Réduire ▲' : 'Plus… ▼'}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                {l.imgs && l.imgs.length > 0 && !open ? (
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: 800,
                      color: C.teal,
                      background: C.tealL,
                      borderRadius: 20,
                      padding: '2px 7px',
                    }}
                  >
                    📎 {l.imgs.length}
                  </span>
                ) : null}
                <span style={{ color: C.muted, fontSize: 13 }}>{open ? '▲' : '✎'}</span>
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginTop: 9,
                paddingTop: 9,
                borderTop: '1px dashed ' + C.border,
              }}
            >
              <div style={{ fontSize: 11, color: C.grey, fontWeight: 600 }}>
                {estBloc(l)
                  ? tr('Prix global')
                  : l.q + ' × ' + DEVISE + ' ' + fmt(+l.p || 0) + ' / ' + l.u}
                {effRem(l) > 0 ? ' · -' + effRem(l) + '%' : ''}
              </div>
              <div style={{ fontSize: 14, fontWeight: 900, color: C.ink }}>
                {DEVISE} {fmt(lineTot(l))}
              </div>
            </div>
            {(() => {
              const man = +l.rem || 0;
              const r = (cl && cl.remises) || {};
              const spec =
                l.ref && Object.prototype.hasOwnProperty.call(r, l.ref) ? +r[l.ref] || 0 : null;
              // La remise écrite sur la ligne fait foi : une offre déjà faite
              // annonçait la remise du client d'aujourd'hui, qu'elle n'a pas.
              if (spec != null && spec > 0 && spec === man)
                return (
                  <div style={{ fontSize: 8.5, color: C.green, fontWeight: 700, marginTop: 4 }}>
                    ✓ Remise client {spec}% appliquée
                  </div>
                );
              if (man > 0 && spec != null)
                return (
                  <div style={{ fontSize: 8.5, color: C.green, fontWeight: 700, marginTop: 4 }}>
                    Remise {man}%
                  </div>
                );
              if (man > 0)
                return (
                  <div style={{ fontSize: 8.5, color: C.green, fontWeight: 700, marginTop: 4 }}>
                    Remise {man}% (manuelle)
                  </div>
                );
              if (!l.ref)
                return (
                  <div style={{ fontSize: 8.5, color: C.muted, marginTop: 4 }}>
                    Ligne libre — pas de remise automatique
                  </div>
                );
              const er = remEff(l);
              return er > 0 ? (
                <div style={{ fontSize: 8.5, color: C.green, fontWeight: 700, marginTop: 4 }}>
                  Remise {er}%
                </div>
              ) : (
                <div style={{ fontSize: 8.5, color: C.muted, marginTop: 4 }}>
                  Aucune remise définie pour cet article
                  {form.clientId ? '' : " — choisis d'abord le client"}
                </div>
              );
            })()}
            {open && (
              <div style={{ marginTop: 11, paddingTop: 11, borderTop: '1px solid ' + C.border }}>
                {/*
                  Le texte de l'article d'abord : c'est ce qu'on vient changer en
                  ouvrant une ligne, surtout quand l'offre a ete redigee pour soi.
                  Titre et description se suivent, en zone de texte simple — ce
                  qui s'ecrit ici part dans un devis signe, sans balise.
                */}
                <div style={lab}>Titre</div>
                <CaseCorrigee
                  value={l.d}
                  onChange={(v) => upd(l.id, 'd', v)}
                  style={{ ..._inp, marginBottom: 10 }}
                />
                <div style={lab}>Description</div>
                <ChampDescription
                  value={texteDescription(l.sd)}
                  onChange={(v) => upd(l.id, 'sd', v)}
                  placeholder="Détails de l’article (optionnel)"
                  fontSize={13}
                />
                {/*
                  Le Pack IA propose la description a partir du titre et de ce
                  qui est deja ecrit ; la proposition remplace le champ, que
                  l'utilisateur relit et corrige avant d'enregistrer.
                */}
                <div style={{ margin: '8px 0 11px' }}>
                  <BoutonRediger
                    genre="devis"
                    titre={l.d}
                    mots={texteDescription(l.sd)}
                    onTexte={(texte) => upd(l.id, 'sd', texte)}
                  />
                </div>
                <div style={{ marginBottom: 11 }}>
                  <MediasLigne
                    devisId={devisId}
                    genre={piece === 'facture' ? 'FACTURE' : 'DEVIS'}
                    cle={l.cle}
                    medias={l.imgs}
                    onChange={(v) => upd(l.id, 'imgs', v)}
                  />
                </div>

                <div style={{ display: 'flex', gap: 9, alignItems: 'flex-end', marginBottom: 10 }}>
                  <div>
                    <div style={lab}>Quantité</div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        border: '1px solid ' + C.border,
                        borderRadius: 10,
                        overflow: 'hidden',
                        width: 138,
                      }}
                    >
                      <button
                        onClick={() => chgQ(l, Math.max(0, (+l.q || 0) - 1))}
                        style={{
                          width: 42,
                          height: 38,
                          border: 'none',
                          background: C.navyXL,
                          fontSize: 20,
                          fontWeight: 800,
                          color: C.navy,
                          cursor: 'pointer',
                        }}
                      >
                        −
                      </button>
                      <input
                        value={l.q}
                        inputMode="decimal"
                        onChange={(e) => chgQ(l, e.target.value)}
                        style={{
                          flex: 1,
                          border: 'none',
                          textAlign: 'center',
                          fontSize: 14,
                          fontWeight: 800,
                          width: 54,
                          color: C.ink,
                        }}
                      />
                      <button
                        onClick={() => chgQ(l, (+l.q || 0) + 1)}
                        style={{
                          width: 42,
                          height: 38,
                          border: 'none',
                          background: C.navyXL,
                          fontSize: 20,
                          fontWeight: 800,
                          color: C.navy,
                          cursor: 'pointer',
                        }}
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={lab}>Prix unitaire ({DEVISE})</div>
                    <input
                      value={l.p}
                      inputMode="decimal"
                      onChange={(e) => upd(l.id, 'p', e.target.value.replace(',', '.'))}
                      style={{ ..._inp }}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 9, marginBottom: 10 }}>
                  <div style={{ width: 110 }}>
                    <ChampUnite
                      value={l.u}
                      onChange={(u) => upd(l.id, 'u', u)}
                      unites={
                        estBloc(l)
                          ? [UNITE_BLOC, ...metierUnites(soc, estMag)]
                          : metierUnites(soc, estMag)
                      }
                    />
                  </div>
                  <div style={{ width: 96 }}>
                    <div style={lab}>Remise %</div>
                    <input
                      value={l.rem || ''}
                      inputMode="decimal"
                      onChange={(e) => upd(l.id, 'rem', e.target.value.replace(',', '.'))}
                      style={{ ..._inp, textAlign: 'center' }}
                    />
                  </div>
                  <div style={{ width: 74 }}>
                    <div style={lab}>TVA</div>
                    <select
                      value={l.tva != null ? l.tva : ''}
                      onChange={(e) =>
                        upd(l.id, 'tva', e.target.value === '' ? null : +e.target.value)
                      }
                      style={{ ..._inp, textAlign: 'center', padding: '7px 2px' }}
                    >
                      <option value="">déf.</option>
                      {tauxTvaProposes(soc, form.date).map((t) => (
                        <option key={t} value={t}>
                          {t}%
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                  <div
                    style={{
                      ...lab,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span>Tarifs dégressifs (prix selon quantité)</span>
                    <span
                      onClick={() => addPalier(l)}
                      style={{ color: C.teal, fontWeight: 800, cursor: 'pointer' }}
                    >
                      ＋ palier
                    </span>
                  </div>
                  {(l.paliers || []).length === 0 ? (
                    <div style={{ fontSize: 8.5, color: C.muted }}>
                      Aucun palier — le prix unitaire reste fixe.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {(l.paliers || [])
                        .slice()
                        .sort((a, b) => +a.q - +b.q)
                        .map((pp, i) => (
                          <div
                            key={i}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              background: C.navyXL,
                              borderRadius: 8,
                              padding: '5px 9px',
                              fontSize: 9.5,
                              fontWeight: 700,
                              color: C.navy,
                            }}
                          >
                            <span>
                              dès {pp.q} {l.u} → {DEVISE} {fmt(+pp.p || 0)}
                            </span>
                            <span
                              onClick={() => delPalier(l, (l.paliers || []).indexOf(pp))}
                              style={{ color: '#c0392b', cursor: 'pointer', fontWeight: 900 }}
                            >
                              ✕
                            </span>
                          </div>
                        ))}
                      {palierApplique(l) && (
                        <div style={{ fontSize: 8.5, color: C.teal, fontWeight: 700 }}>
                          Tarif dégressif appliqué : {DEVISE} {fmt(prixApplique(l))} dès{' '}
                          {palierApplique(l).q} {l.u}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => del(l.id)}
                  style={{
                    width: '100%',
                    background: C.redL,
                    border: 'none',
                    color: C.red,
                    fontSize: 11,
                    fontWeight: 800,
                    padding: 10,
                    borderRadius: 10,
                    cursor: 'pointer',
                  }}
                >
                  🗑 Supprimer cet article
                </button>
                {Array.isArray(l.bloc) && (
                  <button
                    type="button"
                    onClick={() => {
                      setForm((p) => ({ ...p, lignes: defaitLeBloc(p.lignes, l.id) }));
                      setEdId(null);
                    }}
                    style={{
                      width: '100%',
                      marginTop: 8,
                      border: '1px solid ' + C.border,
                      background: C.white,
                      color: C.navy,
                      fontSize: 11,
                      fontWeight: 800,
                      padding: '9px 10px',
                      borderRadius: 10,
                      cursor: 'pointer',
                    }}
                  >
                    {tr('↩️ Défaire le bloc (remettre les articles)')}
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
      {choixBloc && (
        <div
          style={{
            position: 'sticky',
            // Dans l'offre, la barre « Générer l'offre » colle aussi en bas :
            // le bloc se range juste au-dessus, au lieu de passer dessous.
            bottom: facture ? 8 : 132,
            zIndex: 9,
            background: C.navy,
            color: '#fff',
            borderRadius: 12,
            padding: '10px 12px',
            marginBottom: 9,
            boxShadow: '0 6px 18px rgba(15,22,38,.3)',
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 800, marginBottom: 8 }}>
            {trf('{0} article(s) coché(s) · {1} {2}', choixBloc.length, DEVISE, fmt(totalCoche))}
          </div>
          {formBloc && choixBloc.length >= 2 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={labBloc}>
                {tr('Nom du bloc (tu l’écris toi-même)')}
                <input
                  value={formBloc.nom}
                  onChange={(e) => setFormBloc((f) => ({ ...f, nom: e.target.value, erreur: '' }))}
                  placeholder={tr('Ex. : Rénovation salon')}
                  style={champBloc}
                />
              </label>
              <label style={labBloc}>
                {trf('Prix global ({0}, hors TVA)', DEVISE)}
                <input
                  value={formBloc.prix}
                  inputMode="decimal"
                  onChange={(e) => setFormBloc((f) => ({ ...f, prix: e.target.value, erreur: '' }))}
                  style={champBloc}
                />
              </label>
              {formBloc.erreur && (
                <div role="alert" style={{ fontSize: 11, fontWeight: 800, color: '#ffd2cc' }}>
                  {formBloc.erreur}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={faitLeBloc}
                  style={{
                    flex: 1,
                    border: 'none',
                    borderRadius: 9,
                    padding: '11px 8px',
                    fontSize: 12,
                    fontWeight: 800,
                    background: C.green,
                    color: '#fff',
                    cursor: 'pointer',
                  }}
                >
                  {tr('Valider le bloc')}
                </button>
                <button
                  type="button"
                  onClick={() => setFormBloc(null)}
                  style={{
                    border: 'none',
                    borderRadius: 9,
                    padding: '11px 12px',
                    fontSize: 12,
                    fontWeight: 800,
                    background: 'rgba(255,255,255,.15)',
                    color: '#fff',
                    cursor: 'pointer',
                  }}
                >
                  {tr('Annuler')}
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                disabled={choixBloc.length < 2}
                onClick={ouvreLeBloc}
                style={{
                  flex: 1,
                  border: 'none',
                  borderRadius: 9,
                  padding: '10px 8px',
                  fontSize: 11.5,
                  fontWeight: 800,
                  background: choixBloc.length < 2 ? 'rgba(255,255,255,.25)' : C.green,
                  color: '#fff',
                  cursor: choixBloc.length < 2 ? 'default' : 'pointer',
                }}
              >
                {tr('📦 Regrouper en un bloc')}
              </button>
              <button
                type="button"
                onClick={() => setChoixBloc(null)}
                style={{
                  border: 'none',
                  borderRadius: 9,
                  padding: '10px 12px',
                  fontSize: 11.5,
                  fontWeight: 800,
                  background: 'rgba(255,255,255,.15)',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                {tr('Annuler')}
              </button>
            </div>
          )}
        </div>
      )}
      {/* Au magasin, comme à la caisse : chaque code-barres lu ajoute
          l'article à son prix, remise du client comprise. */}
      {estMag && !choixBloc && (
        <div
          style={{
            background: '#f8fafc',
            border: '1.5px solid ' + C.teal,
            borderRadius: 13,
            padding: '10px 11px',
            margin: (form.lignes.length ? 4 : 0) + 'px 0 8px',
          }}
        >
          <div style={{ fontSize: 11.5, fontWeight: 800, color: C.teal, marginBottom: 6 }}>
            {tr('▮▯▮ Scanner comme à la caisse')}
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  scanne(code);
                }
              }}
              inputMode="numeric"
              placeholder={tr('Code-barres (douchette ou clavier)')}
              aria-label={tr('Code-barres')}
              style={{ ..._inp, flex: 1, minWidth: 0 }}
            />
            <ScanCodeBarres compact onCode={scanne} />
          </div>
          <div style={{ fontSize: 9, color: C.grey, marginTop: 5 }}>
            {tr(
              'Chaque bip ajoute l’article à son prix (remise du client comprise). 2 bips = quantité 2.',
            )}
          </div>
          {scanVu && !codeInconnu && (
            <div
              role="status"
              style={{ fontSize: 10, color: C.green, fontWeight: 800, marginTop: 5 }}
            >
              {trf('✓ {0} ajouté', scanVu)}
            </div>
          )}
          {codeInconnu && (
            <div
              role="alert"
              style={{
                marginTop: 7,
                background: '#fff7ed',
                border: '1.5px solid #c2410c',
                borderRadius: 10,
                padding: '8px 10px',
              }}
            >
              <div style={{ fontSize: 10.5, fontWeight: 800, color: '#c2410c' }}>
                {trf('Aucun article du stock avec le code {0}', codeInconnu)}
              </div>
              <div style={{ fontSize: 9, color: '#9a3412', margin: '3px 0 7px', lineHeight: 1.4 }}>
                {tr('Pour la prochaine fois, ajoute ce code-barres à l’article dans Stock.')}
              </div>
              <button
                type="button"
                onClick={() => ouvreLibre('')}
                style={{
                  width: '100%',
                  border: 'none',
                  background: '#c2410c',
                  color: '#fff',
                  borderRadius: 9,
                  padding: 9,
                  fontSize: 11,
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                {tr('✍️ L’ajouter comme article hors catalogue')}
              </button>
            </div>
          )}
        </div>
      )}
      {!showPick && !choixBloc && !libre && (
        <div
          onClick={() => {
            setEdId(null);
            setPickMet('');
            setQ('');
            setOutil('');
            setShowPick(true);
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            padding: 15,
            border: '2px dashed ' + C.green,
            borderRadius: 13,
            background: C.greenL,
            color: C.green,
            fontSize: 13,
            fontWeight: 800,
            cursor: 'pointer',
            marginTop: form.lignes.length ? 4 : 0,
          }}
        >
          ＋ ajouter un article
        </div>
      )}
      {libre && (
        <FicheArticleLibre
          key={libre.nom}
          soc={soc}
          estMag={estMag}
          catalogue={catalogue}
          setCatalogue={setCatalogue}
          nomDepart={libre.nom}
          metier={pickMet}
          libelleAjout={
            facture ? tr('Ajouter à la facture') : 'Ajouter ' + termesOffre(soc, { estMag }).au
          }
          onAjout={ajouteLibre}
          onAnnuler={() => setLibre(null)}
        />
      )}
      {/* « Article hors catalogue » et « corps de métier » sont dans
          « ajouter un article », avec l'assistant et « coller une liste » :
          un seul bouton sous les articles. */}
      {showPick && (
        <div
          style={{
            border: '1px solid ' + C.border,
            borderRadius: 13,
            overflow: 'hidden',
            marginTop: form.lignes.length ? 4 : 0,
            position: 'relative',
            zIndex: 20,
            background: C.white,
          }}
        >
          <div
            style={{
              background: C.navy,
              color: '#fff',
              padding: '10px 12px',
              fontSize: 11,
              fontWeight: 800,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span>➕ Ajouter un article</span>
            <span
              onClick={() => {
                setShowPick(false);
                setPickSel(null);
                setPickMet('');
              }}
              style={{ cursor: 'pointer', fontSize: 15 }}
            >
              ✕
            </span>
          </div>
          {pickSel ? (
            <div style={{ padding: 13, background: '#fbfcfd' }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: C.ink }}>{pickSel.nom}</div>
              <div style={{ fontSize: 9, color: C.grey, margin: '2px 0 10px', lineHeight: 1.4 }}>
                {pickSel.sd ? pickSel.sd + ' · ' : ''}
                {pickSel.metier}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={lab}>Quantité</div>
                  <input
                    aria-label="Quantité"
                    value={draftL.q}
                    inputMode="decimal"
                    onChange={(e) =>
                      setDraftL((d) => ({ ...d, q: e.target.value.replace(',', '.') }))
                    }
                    style={{
                      width: '100%',
                      border: '1px solid ' + C.border,
                      borderRadius: 9,
                      padding: '9px 10px',
                      fontSize: 13,
                      textAlign: 'center',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={lab}>Prix ({DEVISE})</div>
                  <input
                    value={draftL.p}
                    inputMode="decimal"
                    onChange={(e) =>
                      setDraftL((d) => ({ ...d, p: e.target.value.replace(',', '.') }))
                    }
                    style={{
                      width: '100%',
                      border: '1px solid ' + C.border,
                      borderRadius: 9,
                      padding: '9px 10px',
                      fontSize: 13,
                      textAlign: 'center',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>
              <div
                style={{
                  background: C.navyXL,
                  borderRadius: 9,
                  padding: '9px 12px',
                  marginTop: 10,
                }}
              >
                {draftRem > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: 9,
                      color: C.green,
                      fontWeight: 700,
                      marginBottom: 4,
                    }}
                  >
                    <span>✓ Remise client appliquée</span>
                    <span>-{draftRem}%</span>
                  </div>
                )}
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <span style={{ fontSize: 10, color: C.grey, fontWeight: 700 }}>
                    Total de la ligne
                  </span>
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 900,
                      color: draftRem > 0 ? C.green : C.navy,
                    }}
                  >
                    {DEVISE} {fmt((+draftL.q || 0) * draftNet)}
                  </span>
                </div>
              </div>
              {pickSel.stk != null && (+draftL.q || 0) > (+pickSel.stk || 0) && (
                <div
                  role="alert"
                  style={{ fontSize: 10, fontWeight: 800, color: '#b45309', marginTop: 7 }}
                >
                  {trf('⚠ Plus que {0} {1} en stock', +pickSel.stk || 0, pickSel.u || '')}
                </div>
              )}
              <button
                onClick={addFromDraft}
                style={{
                  marginTop: 10,
                  width: '100%',
                  border: 'none',
                  background: C.green,
                  color: '#fff',
                  borderRadius: 10,
                  padding: 12,
                  fontSize: 12.5,
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                {facture
                  ? tr('Ajouter à la facture')
                  : 'Ajouter ' + termesOffre(soc, { estMag }).au}
              </button>
              <div
                onClick={() => setPickSel(null)}
                style={{
                  textAlign: 'center',
                  padding: 9,
                  fontSize: 9.5,
                  color: C.muted,
                  cursor: 'pointer',
                }}
              >
                ← Revenir à la liste
              </div>
            </div>
          ) : (
            <div>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={
                  estMag
                    ? tr('🔎 Cherche : nom de l’article, rayon…')
                    : tr('🔎 Cherche : douche, silicone, mur…')
                }
                aria-label={tr('Chercher un article')}
                style={{
                  width: '100%',
                  border: 'none',
                  borderBottom: '1px solid ' + C.border,
                  padding: 12,
                  fontSize: 16,
                  boxSizing: 'border-box',
                }}
              />
              {/* Les outils restent repliés : un seul s'ouvre à la fois. */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: 6,
                  padding: '9px 12px 0',
                }}
              >
                {[
                  ['assistant', tr('✨ Assistant')],
                  ['coller', tr('📋 Coller une liste')],
                  ['metier', estMag ? tr('🧰 Corps de métier / rayons') : tr('🧰 Corps de métier')],
                ].map(([k, t]) => {
                  const actif = outil === k;
                  return (
                    <button
                      key={k}
                      type="button"
                      aria-expanded={actif}
                      onClick={() => {
                        setOutil(actif ? '' : k);
                        if (k === 'metier' && actif) {
                          setMetChoisis([]);
                          setArtChoisis([]);
                        }
                      }}
                      style={{
                        borderRadius: 11,
                        padding: '9px 3px',
                        fontSize: 10.5,
                        fontWeight: 800,
                        lineHeight: 1.3,
                        cursor: 'pointer',
                        border: '1.5px solid ' + (actif ? C.navy : C.border),
                        background: actif ? C.navy : C.white,
                        color: actif ? '#fff' : C.navy,
                      }}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
              {outil && (
                <div style={{ padding: '10px 12px 0' }}>
                  {/* L'assistant : plusieurs articles d'un coup, écrits ou
                      dictés. Une fois les lignes remplies, la liste se referme
                      pour les montrer. */}
                  {outil === 'assistant' && (
                    <AssistantTravaux
                      piece={piece}
                      form={form}
                      setForm={(maj) => {
                        setForm(maj);
                        setShowPick(false);
                      }}
                      catalogue={catalogueOutils}
                      clients={clients}
                      estMag={estMag}
                      texte={texteAssistant}
                      setTexte={setTexteAssistant}
                    />
                  )}
                  {outil === 'coller' && (
                    <CollerArticles
                      deplie
                      estMag={estMag}
                      form={form}
                      setForm={(maj) => {
                        setForm(maj);
                        setShowPick(false);
                      }}
                      catalogue={catalogueOutils}
                      clients={clients}
                    />
                  )}
                  {outil === 'metier' && (metiersDispo.length > 0 || metiersEnleves.length > 0) && (
                    <div style={{ marginBottom: 10 }}>{contenuMetiers}</div>
                  )}
                </div>
              )}
              {/* Un corps de métier ouvert montre ses articles à cocher : la
                  liste complète se cache, pour ne pas voir deux listes. */}
              {!(outil === 'metier' && metChoisis.length > 0) && (
                <>
                  <div style={{ fontSize: 8, color: C.muted, padding: '6px 12px 0' }}>
                    {dispo.length} article{dispo.length > 1 ? 's' : ''}
                    {ql ? ' trouvé' + (dispo.length > 1 ? 's' : '') : ' au total'}
                  </div>
                  <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => ouvreLibre(q.trim())}
                      style={{
                        padding: 12,
                        borderBottom: '1px solid ' + C.border,
                        cursor: 'pointer',
                        fontSize: 10.5,
                        fontWeight: 800,
                        color: C.navy,
                      }}
                    >
                      {ql
                        ? trf('✍️ « {0} » : article qui n’est pas dans mon catalogue', q.trim())
                        : tr('✍️ Article qui n’est pas dans mon catalogue')}
                    </div>
                    {dispo.length === 0 && !ql && (
                      <div
                        style={{
                          padding: '11px 12px',
                          background: '#fff8e6',
                          borderBottom: '1px solid ' + C.border,
                          fontSize: 9.5,
                          color: '#8a6d00',
                          lineHeight: 1.5,
                        }}
                      >
                        📦 Ton stock est vide. Tape un nom ci-dessus puis « ＋ Créer », ou ajoute
                        d'abord des articles dans <b>Stock</b>.
                      </div>
                    )}
                    {ql &&
                      setCatalogue &&
                      !dispo.some((a) => (a.nom || '').toLowerCase() === ql) && (
                        <div
                          onClick={createReusable}
                          style={{
                            padding: 12,
                            background: C.greenL,
                            cursor: 'pointer',
                            borderBottom: '1px solid ' + C.border,
                          }}
                        >
                          <div style={{ fontSize: 10.5, fontWeight: 800, color: C.green }}>
                            ＋ Créer « {q.trim()} » et l'enregistrer
                          </div>
                          <div style={{ fontSize: 8, color: C.green, marginTop: 1 }}>
                            Réutilisable ensuite dans tous tes devis
                          </div>
                        </div>
                      )}
                    {!ql && recents.length > 0 && (
                      <div>
                        <div
                          style={{
                            fontSize: 7.5,
                            fontWeight: 800,
                            color: C.muted,
                            textTransform: 'uppercase',
                            letterSpacing: 0.4,
                            background: '#f6f7f9',
                            padding: '6px 12px',
                          }}
                        >
                          ★ Récemment utilisés
                        </div>
                        {recents.map((rf) => {
                          const a = dispo.find((x) => x.ref === rf);
                          return a ? (
                            <div
                              key={'r' + a.k}
                              onClick={() => choosePick(a)}
                              style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 9,
                                padding: 12,
                                borderBottom: '1px solid ' + C.border,
                                cursor: 'pointer',
                              }}
                            >
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 11.5, fontWeight: 700, color: C.ink }}>
                                  {a.nom}
                                </div>
                                {a.sd && <div style={{ fontSize: 9, color: C.muted }}>{a.sd}</div>}
                              </div>
                              <div
                                style={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: C.navy,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {DEVISE} {fmt(a.p)}/{a.u}
                              </div>
                            </div>
                          ) : null;
                        })}
                      </div>
                    )}
                    {(() => {
                      const groups = {};
                      dispo.forEach((a) => {
                        (groups[a.metier] = groups[a.metier] || []).push(a);
                      });
                      return Object.keys(groups).map((m) => (
                        <div key={m}>
                          <div
                            style={{
                              fontSize: 7.5,
                              fontWeight: 800,
                              color: C.muted,
                              textTransform: 'uppercase',
                              letterSpacing: 0.4,
                              background: '#f6f7f9',
                              padding: '6px 12px',
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <span>{m}</span>
                            <span>{groups[m].length}</span>
                          </div>
                          {groups[m].map((a) => (
                            <div
                              key={a.k}
                              onClick={() => choosePick(a)}
                              style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 9,
                                padding: 12,
                                borderBottom: '1px solid ' + C.border,
                                cursor: 'pointer',
                              }}
                            >
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 11.5, fontWeight: 700, color: C.ink }}>
                                  {a.nom}
                                </div>
                                {a.sd && (
                                  <div style={{ fontSize: 9, color: C.muted, lineHeight: 1.35 }}>
                                    {a.sd}
                                  </div>
                                )}
                                {a.stk != null && (
                                  <div
                                    style={{
                                      fontSize: 8,
                                      fontWeight: 700,
                                      color: (+a.stk || 0) <= (+a.mn || 0) ? C.red : C.grey,
                                      marginTop: 2,
                                    }}
                                  >
                                    📦 Reste {a.stk} {a.u}
                                    {(+a.stk || 0) <= (+a.mn || 0) ? ' · ⚠ à recommander' : ''}
                                  </div>
                                )}
                              </div>
                              {(() => {
                                const rm = a.ref != null ? remiseFor(a.ref) : 0;
                                if (rm > 0) {
                                  const net = Math.round((+a.p || 0) * (1 - rm / 100) * 100) / 100;
                                  return (
                                    <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                      <div
                                        style={{
                                          fontSize: 8,
                                          color: C.muted,
                                          textDecoration: 'line-through',
                                        }}
                                      >
                                        {DEVISE} {fmt(a.p)}
                                      </div>
                                      <div
                                        style={{ fontSize: 11, fontWeight: 800, color: C.green }}
                                      >
                                        {DEVISE} {fmt(net)}
                                        <span
                                          style={{ fontSize: 7.5, fontWeight: 700, marginLeft: 2 }}
                                        >
                                          -{rm}%
                                        </span>
                                      </div>
                                    </div>
                                  );
                                }
                                return (
                                  <div
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 700,
                                      color: C.navy,
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {DEVISE} {fmt(a.p)}/{a.u}
                                  </div>
                                );
                              })()}
                            </div>
                          ))}
                        </div>
                      ));
                    })()}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
